//TODO : Refactor after ai
/**
 * Hilfsfunktionen der App v2: Bezüge auflösen, Schweizer Schreibweise, Saison- und
 * Cross-Selling-Regeln, Formatierung. Fachliche Annahmen: docs/viacid-app/ANNAHMEN.md.
 */
import {
  datensaetze,
  datensatz,
  objektKlasse,
  type Datenbestand,
  type Datensatz,
  type Wert,
} from "@shared/demo-data/viacid";

export const CHF = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
export const ZAHL = new Intl.NumberFormat("de-CH", {
  maximumFractionDigits: 1,
});
export const fr = (n: number): string => `Fr. ${CHF.format(n)}`;
export const zahl = (n: number): string => ZAHL.format(n);

export function datumCH(iso?: Wert | null): string {
  if (!iso || typeof iso !== "string") return "—";
  const [d, zeit] = iso.split("T");
  const [y, m, t] = d.split("-");
  if (!y || !m || !t) return iso;
  return zeit ? `${t}.${m}.${y} ${zeit.slice(0, 5)}` : `${t}.${m}.${y}`;
}

/** Schweizer Schreibweise für die aus Notion generierten Objektnamen (ANNAHMEN B3). */
export function ch(name: string): string {
  return name
    .replace(/Straßen/g, "Strassen")
    .replace(/Straße/g, "Strasse")
    .replace(/Maßnahme/g, "Massnahme")
    .replace(/Ausmaß/g, "Ausmass")
    .replace(/ß/g, "ss");
}

export const str = (w: Wert | undefined | null): string =>
  w == null ? "" : Array.isArray(w) ? w.join(", ") : String(w);
export const num = (w: Wert | undefined | null): number =>
  typeof w === "number" ? w : Number(w) || 0;

export function ein(w: string | string[] | undefined): string | null {
  return Array.isArray(w) ? (w[0] ?? null) : (w ?? null);
}
export function viele(w: string | string[] | undefined): string[] {
  return Array.isArray(w) ? w : w ? [w] : [];
}

export function ziel(
  b: Datenbestand,
  satz: Datensatz | undefined,
  relation: string,
  slug: string
): Datensatz | undefined {
  const id = ein(satz?.bezuege[relation]);
  return id ? datensatz(b, slug, id) : undefined;
}
export function ziele(
  b: Datenbestand,
  satz: Datensatz | undefined,
  relation: string,
  slug: string
): Datensatz[] {
  return viele(satz?.bezuege[relation])
    .map((id) => datensatz(b, slug, id))
    .filter((d): d is Datensatz => Boolean(d));
}
/** Alle Sätze einer Klasse, die über `relation` auf `id` zeigen. */
export function mit(
  b: Datenbestand,
  slug: string,
  relation: string,
  id: string
): Datensatz[] {
  return datensaetze(b, slug).filter((d) =>
    viele(d.bezuege[relation]).includes(id)
  );
}

export function zustandFarbe(status: string | null | undefined): string {
  if (!status) return "";
  if (
    /Gewonnen|Angenommen|Bezahlt|Abgeschlossen|Erledigt|Freigegeben|Ausgeführt|Zugestellt|Behoben|Bewertet|Bestätigt/.test(
      status
    )
  )
    return "green";
  if (/Verloren|Abgelehnt|abgesagt|Verworfen|Gemahnt|Korrektur/.test(status))
    return "red";
  if (
    /bereit|Versendet|Terminiert|Qualifiziert|Geklärt|übermittelt/.test(status)
  )
    return "teal";
  if (
    /Erstellung|Entwurf|Erteilt|Geplant|Identifiziert|Eingegangen|Offen|Aufnahme|Aufgenommen|Gemeldet|Erfassung/.test(
      status
    )
  )
    return "orange";
  return "";
}

// ------------------------------------------------ Saison & Cross-Selling (ANNAHMEN C1, C2)
export interface Saison {
  name: string;
  fenster: string;
  startMonat: number;
  massnahmen: string[];
  baustoffe: string[];
}
export const SAISONS: Saison[] = [
  {
    name: "Winter",
    fenster: "Winter (Okt–Mär)",
    startMonat: 10,
    massnahmen: ["Rissverguss", "Kleinreparaturen", "Schlaglöcher"],
    baustoffe: ["Vergussmasse", "Kaltasphalt"],
  },
  {
    name: "Frühjahr",
    fenster: "Frühjahr (Apr–Mai)",
    startMonat: 4,
    massnahmen: ["Randsteinfugensanierung"],
    baustoffe: ["Fugenmasse", "Haftvermittler"],
  },
  {
    name: "Sommer",
    fenster: "Sommer (Jun–Mitte Sep)",
    startMonat: 6,
    massnahmen: ["Oberflächenbehandlung", "Grossflächen"],
    baustoffe: ["Bitumenemulsion", "Splitt"],
  },
];
/** Laufende Saison (bis 8 Wochen nach Start) oder nächste Saison mit Wochen bis zum Beginn. */
export function naechsteSaison(heute = new Date()): {
  saison: Saison;
  wochen: number;
  laeuft: boolean;
} {
  const jahr = heute.getFullYear();
  const starts = SAISONS.flatMap((s) =>
    [-1, 0, 1].map((plus) => ({
      saison: s,
      start: new Date(jahr + plus, s.startMonat - 1, 1),
    }))
  );
  const woche = 7 * 86400000;
  const laufend = starts
    .filter((k) => k.start <= heute && +heute - +k.start < 8 * woche)
    .sort((a, b) => +b.start - +a.start)[0];
  if (laufend)
    return {
      saison: laufend.saison,
      wochen: Math.max(1, Math.round((+heute - +laufend.start) / woche)),
      laeuft: true,
    };
  const n = starts
    .filter((k) => k.start > heute)
    .sort((a, b) => +a.start - +b.start)[0];
  return {
    saison: n.saison,
    wochen: Math.round((+n.start - +heute) / woche),
    laeuft: false,
  };
}

export type Potenzial = {
  kunde: Datensatz;
  art: "Baustoffe" | "Dienstleistung";
  grund: string;
};
/** Cross-Selling-Regel aus ANNAHMEN C2 auf den Beispieldaten. */
export function crossSellingPotenziale(b: Datenbestand): Potenzial[] {
  const out: Potenzial[] = [];
  for (const k of datensaetze(b, "kunde")) {
    const auftraege = mit(b, "auftrag", "auftrag-von-kunde", k.id);
    const dienst = auftraege.some(
      (a) => a.werte.auftragsart === "Dienstleistung"
    );
    const handel =
      auftraege.some((a) => a.werte.auftragsart !== "Dienstleistung") ||
      mit(b, "bestellung", "bestellung-von-kunde", k.id).length > 0;
    const bezug = str(k.werte.baustoffbezug);
    if (dienst && bezug !== "Viacid")
      out.push({
        kunde: k,
        art: "Baustoffe",
        grund:
          bezug === "Wettbewerb"
            ? "bezieht Dienstleistung, kauft Baustoffe beim Wettbewerb"
            : "bezieht Dienstleistung, Baustoffbezug unbekannt",
      });
    else if (handel && !dienst)
      out.push({
        kunde: k,
        art: "Dienstleistung",
        grund: "kauft Baustoffe, noch kein Dienstleistungsauftrag",
      });
  }
  return out;
}

/** Umsatzsplit eines Kunden aus Auftragspositionen (Beispieldaten). */
export function umsatzSplit(
  b: Datenbestand,
  kundeId: string
): { dienst: number; handel: number } {
  let dienst = 0;
  let handel = 0;
  for (const a of mit(b, "auftrag", "auftrag-von-kunde", kundeId)) {
    const summe = mit(
      b,
      "auftragsposition",
      "auftragsposition-gehoert-zu-auftrag",
      a.id
    ).reduce((s, p) => s + num(p.werte.menge) * num(p.werte.einzelpreis), 0);
    if (a.werte.auftragsart === "Dienstleistung") dienst += summe;
    else handel += summe;
  }
  return { dienst, handel };
}

export function angebotsSumme(b: Datenbestand, angebotId: string): number {
  return mit(
    b,
    "angebotsposition",
    "angebotsposition-gehoert-zu-angebot",
    angebotId
  ).reduce(
    (s, p) =>
      s +
      num(p.werte.menge) *
        num(p.werte.einzelpreis) *
        (1 - num(p.werte.rabatt) / 100),
    0
  );
}

export function tageSeit(iso?: Wert | null): number {
  if (!iso || typeof iso !== "string") return 0;
  return Math.max(0, Math.floor((Date.now() - +new Date(iso)) / 86400000));
}

/** Stationierung „Station 0+020 bis 0+105“ → Meter (ANNAHMEN C5). */
export function stationMeter(
  position: Wert | undefined
): { von: number; bis: number } | null {
  const s = str(position);
  const m = [...s.matchAll(/(\d+)\+(\d{3})/g)].map(
    (x) => Number(x[1]) * 1000 + Number(x[2])
  );
  if (m.length === 0) {
    const n = s.match(/(\d+)\s*m/);
    return n ? { von: Number(n[1]), bis: Number(n[1]) + 5 } : null;
  }
  return { von: m[0], bis: m[1] ?? m[0] + 5 };
}

export function klassenName(slug: string): string {
  return ch(objektKlasse(slug)?.name ?? slug);
}

/** Eindeutige Prototyp-ID (Zeitstempel) – ausserhalb der Komponenten, damit der Render rein bleibt. */
export function neueId(prefix: string, suffix = ""): string {
  return `${prefix}-${Date.now()}${suffix}`;
}
export const jetztIso = (): string => new Date().toISOString().slice(0, 16);
